import { type FC, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { TextArea } from '@/components/commons/textarea'
import { InputHelpText } from '@/components/ui/input-help-text'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from '@/components/ui/select'
import { cn } from '@/helpers/utils'
import { Trans, useTranslation } from '@/translations/i18n'
import type { QuestionTier, ScoreFormData, VettingQuestion } from '../types'
import {
	HARD_STAKE_CODES,
	QUESTION_TRANSLATIONS,
	RESIDENCE_TYPE_OPTIONS_LABELS,
	sortQuestionsByCode,
	UNSURE_VALUES
} from '../utils'
import { QualityQuestion } from './quality-question'
import { TableStakeBanner } from './table-stake-banner'
import { TableStakeQuestion } from './table-stake-question'
import { YesNoQuestion } from './yes-no-question'

const NOTES_MAX_LENGTH = 300
const METADATA_CODES = { RT: 'RT', TSN: 'TSN' } as const

interface ScoreFormPanelProps {
	questions: VettingQuestion[]
	formDisabled?: boolean
	showCounter?: boolean
	mmViewOnly?: boolean
}

function groupByTier(questions: VettingQuestion[]) {
	const groups: Record<QuestionTier, VettingQuestion[]> = {
		metadata: [],
		blocker: [],
		table_stake: [],
		scored: [],
		holistic: []
	}
	for (const q of questions) {
		groups[q.tier].push(q)
	}

	const sortedTableStake = sortQuestionsByCode(groups.table_stake)

	return {
		blocker: sortQuestionsByCode(groups.blocker),
		table_stake: sortedTableStake.filter(
			(q) => q.code.toUpperCase() !== METADATA_CODES.TSN
		),
		table_stake_note: sortedTableStake.find(
			(q) => q.code.toUpperCase() === METADATA_CODES.TSN
		),
		scored: sortQuestionsByCode(groups.scored),
		holistic: sortQuestionsByCode(groups.holistic),
		metadata: sortQuestionsByCode(groups.metadata)
	}
}

export const ScoreFormPanel: FC<ScoreFormPanelProps> = ({
	questions,
	formDisabled,
	showCounter = false,
	mmViewOnly = false
}) => {
	const { t } = useTranslation()

	const {
		control,
		watch,
		formState: { errors }
	} = useFormContext<ScoreFormData>()
	const formData = watch()

	const tiers = useMemo(() => groupByTier(questions), [questions])

	const rtQuestion = useMemo(
		() =>
			tiers.metadata.find(
				(q) => q.code.toUpperCase() === METADATA_CODES.RT
			),
		[tiers.metadata]
	)
	const tsnQuestion = tiers.table_stake_note

	const tsnQuestionValue = tsnQuestion
		? formData.answers?.[tsnQuestion.id]
		: undefined

	const hardStakeQuestionIds = useMemo(
		() =>
			tiers.table_stake
				.filter((q) => HARD_STAKE_CODES.has(q.code.toUpperCase()))
				.map((q) => q.id),
		[tiers.table_stake]
	)

	const isTsnRequired = useMemo(() => {
		const answers = formData.answers ?? {}
		return hardStakeQuestionIds.some((id) => {
			const value = answers[id]
			return Boolean(value) && UNSURE_VALUES.has(value)
		})
	}, [formData, hardStakeQuestionIds])

	const answeredCount = useMemo(() => {
		const answers = formData.answers ?? {}
		return Object.entries(answers).filter(
			([id, value]) => Boolean(value) && id !== tsnQuestion?.id
		).length
	}, [formData, tsnQuestion?.id])

	const getTr = (code: string) => QUESTION_TRANSLATIONS[code.toLowerCase()]

	return (
		<div
			className={cn(
				'relative flex flex-col gap-0 pr-6 pb-6 pl-8',
				!showCounter && 'pt-6'
			)}
		>
			{/* Progress header */}
			{showCounter && (
				<div className='sticky top-0 z-1 flex flex-col gap-1 bg-neutral-white pt-6 pb-4'>
					<p className='font-semibold text-neutral-900 text-sm'>
						{t('luxe_submissions:score_form.questions_answered', {
							count: answeredCount,
							total: questions.length - 1 // exclude table_stake_note from count
						})}
					</p>
					<p className='text-neutral-500 text-sm'>
						{t('luxe_submissions:score_form.answer_all_questions')}
					</p>
				</div>
			)}

			<div className='flex flex-col gap-4'>
				{mmViewOnly && !!tsnQuestionValue && (
					<div className='order-1 flex flex-col gap-2 text-base'>
						<p className='font-bold text-neutral-900'>
							{t('luxe_submissions:vv_table_stake_note')}
						</p>
						<span className='rounded-lg border border-neutral-300 bg-neutral-50 px-3.5 py-3 text-neutral-500'>
							{tsnQuestionValue}
						</span>
					</div>
				)}

				{/* Residence Type (metadata) */}
				{rtQuestion && (
					<Controller
						control={control}
						key={rtQuestion.id}
						name={`answers.${rtQuestion.id}`}
						render={({ field }) => {
							return (
								<div className='order-3 flex flex-col gap-2.5'>
									<p className='font-bold text-base'>
										{t(
											'luxe_submissions:score_form.residence_type'
										)}
									</p>
									<Select
										inputRef={field.ref}
										onValueChange={(val) => {
											if (val) {
												field.onChange(val)
											}
										}}
										value={field.value ?? ''}
									>
										<SelectTrigger
											className='w-full'
											disabled={formDisabled}
										>
											<SelectValue>
												{field.value
													? t(
															RESIDENCE_TYPE_OPTIONS_LABELS[
																field.value as keyof typeof RESIDENCE_TYPE_OPTIONS_LABELS
															]
														)
													: t(
															'luxe_submissions:score_form.select_option'
														)}
											</SelectValue>
										</SelectTrigger>
										<SelectContent className='w-(--anchor-width)'>
											{rtQuestion.answerOptions.map(
												(option) => {
													return (
														<SelectItem
															key={option}
															value={option}
														>
															{t(
																RESIDENCE_TYPE_OPTIONS_LABELS[
																	option as keyof typeof RESIDENCE_TYPE_OPTIONS_LABELS
																]
															)}
														</SelectItem>
													)
												}
											)}
										</SelectContent>
									</Select>

									<InputHelpText
										error={
											errors.answers?.[rtQuestion.id]
												?.message
										}
									/>
								</div>
							)
						}}
						rules={{
							required: t('errors:field_required')
						}}
					/>
				)}

				{/* Tier 1: Blockers */}
				{tiers.blocker.length > 0 && (
					<div className='order-4 flex flex-col gap-4'>
						<div className='flex flex-col gap-2'>
							<h2 className='font-bold text-base text-neutral-900'>
								{t('luxe_submissions:score_form.tier1_title')}
							</h2>
							<p className='text-neutral-500 text-sm'>
								{t(
									'luxe_submissions:score_form.tier1_description'
								)}
							</p>
						</div>

						{tiers.blocker.map((question) => {
							const tr = getTr(question.code)
							return (
								<Controller
									control={control}
									key={question.id}
									name={`answers.${question.id}`}
									render={({ field }) => (
										<YesNoQuestion
											answerOptions={
												question.answerOptions
											}
											description={
												tr?.description
													? t(tr.description)
													: undefined
											}
											disabled={formDisabled}
											errorMessage={
												errors.answers?.[question.id]
													?.message
											}
											name={field.name}
											onChange={field.onChange}
											ref={field.ref}
											title={
												tr ? t(tr.title) : question.code
											}
											value={field.value}
										/>
									)}
									rules={{
										required: t('errors:field_required')
									}}
								/>
							)
						})}
					</div>
				)}

				{/* Tier 2: Table stakes */}
				{tiers.table_stake.length > 0 && (
					<>
						<div className='order-5 flex flex-col gap-4'>
							<div className='flex flex-col gap-2'>
								<h2 className='mt-4 font-bold text-base text-neutral-900'>
									{t(
										'luxe_submissions:score_form.tier2_title'
									)}
								</h2>

								<div className='flex flex-col gap-4'>
									{tiers.table_stake.map((question) => {
										const tr = getTr(question.code)
										return (
											<Controller
												control={control}
												key={question.id}
												name={`answers.${question.id}`}
												render={({ field }) => (
													<TableStakeQuestion
														answerOptions={
															question.answerOptions
														}
														code={question.code.toLowerCase()}
														description={
															tr?.description
																? t(
																		tr.description
																	)
																: question.description
														}
														disabled={formDisabled}
														errorMessage={
															errors.answers?.[
																question.id
															]?.message
														}
														onChange={
															field.onChange
														}
														ref={field.ref}
														title={
															tr?.shortLabel &&
															tr?.longLabel
																? `${t(tr.shortLabel)}. ${t(tr.longLabel)}`
																: question.code
														}
														value={field.value}
													/>
												)}
												rules={{
													required: t(
														'errors:field_required'
													)
												}}
											/>
										)
									})}
								</div>
							</div>
						</div>

						{/* Table Stake Summary Banners */}
						<TableStakeBanner
							className={mmViewOnly ? 'order-2' : 'order-6'}
							formAnswers={formData.answers ?? {}}
							// hideActionPrompts shows instructions from table stakes only for vetters
							hideActionPrompts={!showCounter}
							questions={questions}
						/>

						{/* Table Stakes Notes (metadata) */}
						{tsnQuestion && !mmViewOnly && (
							<div className='order-7 flex flex-col gap-2'>
								<div className='flex flex-col gap-1'>
									<p className='font-bold text-base text-neutral-900'>
										{t(
											'luxe_submissions:score_form.table_stakes_notes_title'
										)}
									</p>
									<p className='text-neutral-900 text-sm'>
										{t(
											'luxe_submissions:score_form.table_stakes_notes_description'
										)}
									</p>
								</div>
								<Controller
									control={control}
									name={`answers.${tsnQuestion.id}`}
									render={({ field }) => (
										<TextArea
											disabled={formDisabled}
											errorMessage={
												errors.answers?.[tsnQuestion.id]
													?.message
											}
											helpText={`${(field.value ?? '').length}/${NOTES_MAX_LENGTH}`}
											maxLength={NOTES_MAX_LENGTH}
											onChange={field.onChange}
											placeholder={t(
												'luxe_submissions:score_form.notes_placeholder'
											)}
											ref={field.ref}
											rows={6}
											value={field.value}
										/>
									)}
									rules={{
										validate: (value) => {
											if (!isTsnRequired) {
												return true
											}
											return value &&
												value.trim().length > 0
												? true
												: t('errors:field_required')
										}
									}}
								/>
							</div>
						)}
					</>
				)}

				{/* Tier 3: Quality Assessment (scored) */}
				{tiers.scored.length > 0 && (
					<div className='order-8 mt-4 flex flex-col gap-4'>
						<div className='flex flex-col gap-2'>
							<h2 className='font-bold text-base text-neutral-900'>
								{t('luxe_submissions:score_form.tier3_title')}
							</h2>
							<p className='text-neutral-900 text-sm'>
								<Trans
									components={{ strong: <b /> }}
									i18nKey='luxe_submissions:score_form.tier3_legend'
								/>
							</p>
						</div>

						{tiers.scored.map((question) => {
							const tr = getTr(question.code)
							return (
								<Controller
									control={control}
									key={question.id}
									name={`answers.${question.id}`}
									render={({ field }) => (
										<QualityQuestion
											answerOptions={
												question.answerOptions
											}
											description={
												tr?.description
													? t(tr.description)
													: question.description
											}
											disabled={formDisabled}
											errorMessage={
												errors.answers?.[question.id]
													?.message
											}
											name={field.name}
											onChange={field.onChange}
											ref={field.ref}
											title={
												tr ? t(tr.title) : question.code
											}
											value={field.value}
										/>
									)}
									rules={{
										required: t('errors:field_required')
									}}
								/>
							)
						})}
					</div>
				)}

				{/* Tier 4: Holistic */}
				{tiers.holistic.length > 0 && (
					<div className='order-9 mt-4 flex flex-col gap-4'>
						{tiers.holistic.map((question) => {
							const tr = getTr(question.code)
							return (
								<Controller
									control={control}
									key={question.id}
									name={`answers.${question.id}`}
									render={({ field }) => (
										<QualityQuestion
											answerOptions={
												question.answerOptions
											}
											description={
												tr?.description
													? t(tr.description)
													: question.description
											}
											disabled={formDisabled}
											errorMessage={
												errors.answers?.[question.id]
													?.message
											}
											name={field.name}
											onChange={field.onChange}
											ref={field.ref}
											title={
												tr ? t(tr.title) : question.code
											}
											value={field.value}
										/>
									)}
									rules={{
										required: t('errors:field_required')
									}}
								/>
							)
						})}
					</div>
				)}
			</div>
		</div>
	)
}
